import React, { ReactElement } from 'react';
import { EngagementMapsState } from '../types';
import { Box, Button, Flex, InlineLink, Typography } from '@fullstory/components';
import { FormattedMessage, useIntl } from 'react-intl';

const DefaultTextVariant = 'body3';
const DefaultTextColor = 'neutrals-n8';

export interface EngagementMapsStatusProps {
  /**
   * The name of the Engagement Map.
   */
  name: string;
  /**
   * The current state of loading the Engagement Maps data. This is used to determine what message to display.
   */
  state: EngagementMapsState;
  /**
   * The React Node to display when data has been received.
   */
  data: ReactElement | null;
  /**
   * The message to display for the warning. This is optional. However, if this is not provided, the caller
   * SHOULD NOT set state to 'warning_displayed'.
   */
  warningMessage?: ReactElement;
  /**
   * A callback provided to update the state if the user wants to bypass a warning. This is optional. However,
   * if this is not provided, the caller SHOULD NOT set state to 'warning_displayed'.
   */
  onWarningButtonClick?: () => void;
}

/**
 * A component responsible for displaying the loading status for an Engagement Map.
 */
export const EngagementMapsStatus: React.FC<EngagementMapsStatusProps> = ({
  name,
  state,
  data,
  warningMessage,
  onWarningButtonClick,
}) => {
  const intl = useIntl();
  let message = null;
  switch (state) {
    case 'to_load':
    case 'loading':
      message = intl.formatMessage(
        { defaultMessage: 'Building {name} data...', id: 'tLTQNi' },
        { name },
      );
      break;
    case 'data':
      // The caller will pass in a custom component for display via the {data} prop in this case.
      break;
    case 'no_data':
      message = intl.formatMessage({
        defaultMessage: "We didn't find any data for this page.",
        id: 'F3B3gK',
      });
      break;
    case 'network_error':
      message = (
        <>
          <FormattedMessage
            defaultMessage="An error occurred while retrieving {name} data. Try refreshing the page. If the problem persists, please reach out to"
            id="wshreL"
            values={{ name }}
          />

          <br />
          <InlineLink href="mailto:support@fullstory.com" newTab>
            support@fullstory.com
          </InlineLink>
        </>
      );
      break;
    case 'warning_displayed':
      message = warningMessage ? warningMessage : null;
  }

  if (state === 'data') {
    return (
      <Box
        data-component="EngagementMapsStatus"
        data-fs="engagement-maps-status"
        data-state={state}
      >
        {data}
      </Box>
    );
  } else {
    return (
      <Box
        data-component="EngagementMapsStatus"
        data-fs="engagement-maps-status"
        data-state={state}
        padding={[24, 32, 24, 32]}
      >
        <Flex column>
          <Typography variant={DefaultTextVariant} color={DefaultTextColor} textAlign="center">
            {message}
          </Typography>
          {state === 'warning_displayed' && (
            <Box data-fs="engagement-maps-status-warning-button" paddingTop={24}>
              <Button
                size="medium"
                variant="outline"
                onClick={e => {
                  onWarningButtonClick?.();
                }}
              >
                <FormattedMessage defaultMessage="Show me anyway" id="mLOt8p" />
              </Button>
            </Box>
          )}
        </Flex>
      </Box>
    );
  }
};
